// ADR 5.9.26p §3/§4 — where a browser's tokens live, and the one place a
// refresh may happen.
//
// The phones keep both tokens in LMDB and serialise refreshes behind
// `refresh_lock` (`rust-core/src/client/auth_refresh.rs`, `client/http.rs:231-248`).
// A tab has neither a keychain nor a mutex, so §3 makes the storage trade
// explicit — access JWT in memory, refresh token in `localStorage` — and §4
// rebuilds the mutex out of one shared promise.
//
// Nothing here calls `fetch`. The refresh request arrives as a
// {@link RefreshTransport}, which is what lets this module sit *below*
// `api/http.ts` in the import graph rather than beside it in a cycle: `http.ts`
// supplies the call, `tokenStore.ts` owns when it may happen and what its
// failure means.

import type { AuthResponse } from '../api/auth'
import { tokenRefreshFailed, tokenRefreshTriggered } from '../telemetry/auth'
import { signedOut } from '../telemetry/identity'
import { NetworkUnreachableError } from '../offline/networkFailure'
import { clearSignedInHint, setSignedInHint } from './signedInHint'

/**
 * The only *credential* key this module writes (5.9.26p AC5: the access token
 * is never persisted). Not the only key the client writes — the storage
 * inventory in `docs/security/1.0.13+15-web-review.md` (ADR 24.9.26h W5-2)
 * lists every one, and which of them sign-out clears.
 */
export const REFRESH_TOKEN_KEY = 'lingara.web.refresh.v1'

/** Dispatched on `window` when the session ends — exactly once (AC4). */
export const SIGNED_OUT_EVENT = 'lingara:signed-out'

/**
 * Dispatched on `window` whenever an `AuthResponse` is adopted, carrying the
 * identity it confirmed.
 *
 * §4: *every* refresh returns a full `AuthResponse`, so `emailVerified` is
 * refreshed with it — including a refresh nobody asked for, triggered behind
 * some unrelated request by the 401 interceptor. Without this event the session
 * context would learn the new value only on the one path that awaits the
 * response itself, and a learner who verified their address in another tab
 * would keep seeing the interstitial until they happened to focus this one.
 */
export const SESSION_REFRESHED_EVENT = 'lingara:session-refreshed'

/** What {@link SESSION_REFRESHED_EVENT} carries. */
export interface SessionIdentity {
  userId: string
  emailVerified: boolean
}

/**
 * Treat a JWT as expired this many seconds early, so a request that is already
 * on the wire when the clock rolls over does not come back 401.
 */
const DEFAULT_SKEW_SECONDS = 30

/** `POST /auth/refresh`, supplied by `api/http.ts`. */
export type RefreshTransport = (refreshToken: string) => Promise<AuthResponse>

let accessToken: string | null = null
let inFlight: Promise<AuthResponse | null> | null = null
// Latched by a refused refresh. The refusal is the server's final word on this
// token family, so a second attempt would only re-ask a question already
// answered — which is how a refresh loop starts (AC4).
let refused = false

/** The persisted refresh token, or `null` when this browser has no session. */
export function readRefreshToken(): string | null {
  return window.localStorage.getItem(REFRESH_TOKEN_KEY)
}

/** The access JWT currently in memory. Never persisted (AC5). */
export function getAccessToken(): string | null {
  return accessToken
}

/**
 * Adopt an `AuthResponse` — from login, register, social **or** refresh, which
 * all return the same envelope.
 */
export function storeSession(response: AuthResponse): void {
  accessToken = response.token
  // Rotation: the server hands back a fresh refresh token on every refresh. An
  // absent one means "keep the one you have", not "you are signed out" — the
  // same fallback `parse_refresh_response` makes in rust-core.
  if (response.refresh_token) {
    window.localStorage.setItem(REFRESH_TOKEN_KEY, response.refresh_token)
  }
  // ADR 27.10.26e §2 — renewed on every adoption, refresh included.
  setSignedInHint(document, window.location.hostname)
  refused = false
  const identity: SessionIdentity = {
    userId: response.user_id,
    emailVerified: response.email_verified,
  }
  window.dispatchEvent(new CustomEvent<SessionIdentity>(SESSION_REFRESHED_EVENT, { detail: identity }))
}

/** Forget both tokens and the refusal latch, quietly. */
export function clearSession(): void {
  accessToken = null
  inFlight = null
  refused = false
  window.localStorage.removeItem(REFRESH_TOKEN_KEY)
  // ADR 27.10.26e §2 — `signOutLocally` reaches here on every sign-out path.
  clearSignedInHint(document, window.location.hostname)
}

/**
 * Clear both tokens and announce it — once. A second call while already signed
 * out is silent, so five concurrent callers meeting one refusal produce one
 * `signed-out` event rather than five.
 *
 * ADR 6.9.26j §1b: `reset()` belongs *here*, on the shared function, because
 * both the voluntary and the involuntary sign-out must forget the person — and
 * on a browser that matters more than on a phone, where two people sharing a
 * device would otherwise become one person in the data (13.8.26a D1). This is
 * the exact opposite of `token_refresh_failed`, which must **not** be emitted
 * from here for the same reason: this function is on both paths.
 */
export function signOutLocally(): void {
  const alreadySignedOut = refused
  clearSession()
  refused = true
  if (!alreadySignedOut) {
    signedOut()
    window.dispatchEvent(new Event(SIGNED_OUT_EVENT))
  }
}

function base64UrlDecode(segment: string): string {
  const padded = segment.replace(/-/g, '+').replace(/_/g, '/')
  return atob(padded.padEnd(Math.ceil(padded.length / 4) * 4, '='))
}

/** The `exp` claim in seconds, or `null` when the token is not a readable JWT. */
export function jwtExpiry(token: string): number | null {
  const payload = token.split('.')[1]
  if (payload === undefined) return null
  try {
    const claims = JSON.parse(base64UrlDecode(payload)) as { exp?: number }
    return typeof claims.exp === 'number' ? claims.exp : null
  } catch {
    return null
  }
}

/**
 * Is `token` at or past its `exp`, allowing `skewSeconds` of drift?
 *
 * A token whose `exp` cannot be read counts as expired: refreshing an opaque
 * string costs one request, whereas trusting it costs a 401 on a real call.
 */
export function isJwtExpired(token: string, skewSeconds: number = DEFAULT_SKEW_SECONDS): boolean {
  const exp = jwtExpiry(token)
  if (exp === null) return true
  return exp <= Date.now() / 1000 + skewSeconds
}

/**
 * The single-flight refresh (§4). Concurrent callers await one promise, so the
 * caller that created it is the only one that touches the network (AC3).
 *
 * ADR 6.9.26j §1 hangs the two `token_refresh_*` events off this function and
 * nothing else, because these two branches are the **involuntary** paths and
 * nothing else reaches them. Exactly-once survives for free: the single flight
 * above and the `refused` latch mean five concurrent callers meeting one
 * refusal produce one emit, the same property that gives `signed-out` its
 * single dispatch.
 */
export function refreshTokens(transport: RefreshTransport): Promise<AuthResponse | null> {
  if (inFlight !== null) return inFlight
  if (refused) return Promise.resolve(null)

  const refreshToken = readRefreshToken()
  if (refreshToken === null) {
    tokenRefreshFailed('absent')
    signOutLocally()
    return Promise.resolve(null)
  }

  tokenRefreshTriggered()
  inFlight = transport(refreshToken)
    .then((response) => {
      storeSession(response)
      return response
    })
    .catch((error: unknown) => {
      // ADR 23.9.26v §2 — a request the server never saw is not a refusal. The
      // token, the latch and the silence are all kept, and the rejection flows
      // on so no caller reads it as the `null` that means "signed out". No
      // `token_refresh_failed` either: the token did not fail, and the bare
      // trigger above is the "server unreachable" ratio on its own. ADR
      // 2.10.26s §2: a timed-out refresh is a `RequestTimeoutError`, a subclass,
      // so it lands here too and `finally` frees the queue behind it.
      if (error instanceof NetworkUnreachableError) throw error
      // Any refusal — 401, 403, or an answer this tab cannot read — ends the
      // session here rather than being retried behind the user's back.
      tokenRefreshFailed('refused')
      signOutLocally()
      return null
    })
    .finally(() => {
      inFlight = null
    })
  return inFlight
}

/**
 * The token to send, refreshing first when the one in hand is (nearly) expired.
 */
export async function ensureFreshToken(transport: RefreshTransport): Promise<string | null> {
  const token = accessToken
  if (token !== null && !isJwtExpired(token)) return token
  return (await refreshTokens(transport))?.token ?? null
}

/**
 * A 401 came back for `staleToken`.
 *
 * Re-read before refreshing, exactly as `http.rs:231-248` does after acquiring
 * the lock: a concurrent caller may already have rotated the family while this
 * request was on the wire, in which case there is nothing to do but retry with
 * what it left behind.
 */
export async function refreshAfterUnauthorized(
  transport: RefreshTransport,
  staleToken: string,
): Promise<string | null> {
  if (accessToken !== null && accessToken !== staleToken) return accessToken
  return (await refreshTokens(transport))?.token ?? null
}
